import React from 'react';
import { Box, Alert, IconButton, Typography } from '@mui/joy';
import { X, CheckCircle, XCircle as AlertCircle, Info, Warning as AlertTriangle } from '@phosphor-icons/react';
import { useNotification } from '../contexts/NotificationContext';
import type { NotificationType } from '../contexts/NotificationContext';

const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useNotification();

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <AlertCircle size={20} />;
      case 'info':
        return <Info size={20} />;
      case 'warning':
        return <AlertTriangle size={20} />;
    }
  };

  const getColor = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      case 'info':
        return 'primary';
      case 'warning':
        return 'warning';
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: '320px',
        maxWidth: '500px',
        width: '90vw',
        pointerEvents: 'none',
      }}
    >
      {notifications.map((notification) => (
        <Box
          key={notification.id}
          sx={{
            pointerEvents: 'auto',
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              from: {
                opacity: 0,
                transform: 'translateY(-20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
            '&.removing': {
              animation: 'slideOut 0.3s ease-in forwards',
            },
            '@keyframes slideOut': {
              from: {
                opacity: 1,
                transform: 'translateY(0)',
              },
              to: {
                opacity: 0,
                transform: 'translateY(-20px)',
              },
            },
          }}
        >
          <Alert
            color={getColor(notification.type)}
            startDecorator={getIcon(notification.type)}
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color={getColor(notification.type)}
                onClick={() => removeNotification(notification.id)}
                sx={{ ml: 'auto' }}
              >
                <X size={16} />
              </IconButton>
            }
            sx={{
              boxShadow: 'lg',
              border: '1px solid',
              borderColor: 'divider',
              pr: 0.5,
            }}
          >
            <Typography level="body-sm" sx={{ wordBreak: 'break-word' }}>
              {notification.message}
            </Typography>
          </Alert>
        </Box>
      ))}
    </Box>
  );
};

export default NotificationContainer;